import './styles.scss'

import React, { useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'

import I18n from 'lib/i18n'
import railsContext from 'lib/selectors/rails-context'
import PropTypes from 'prop-types'
import { Map as PigeonMap } from 'pigeon-maps'

import preloadImages from 'lib/utils/preload-images'
import GeolocateButton from 'lib/components/geolocate-button'
import Close from 'lib/icons/close'

import Attribution from './attribution'

import { MAPTILER_ACCESS_TOKEN } from '~/scenes/settings'

const mapTilerLocales = {
  ar: 'c82a66e2-6bfd-40cd-a9cb-91933d7d1126',
  nl: '09bec3bd-b26d-4d6e-afb7-57d451a735a4',
  en: '3196ca3f-4c0e-4b16-9756-d80cdcd22fc3',
  et: '21ca85de-3cfd-4533-8ce1-a813289dee5a',
  fr: 'afe1c010-fb23-40e6-8d3c-251fe88eaac2',
  de: '421c14c0-2c2c-4220-8573-4e9be1a49d34',
  es: 'e216b98b-c5e6-4449-8b2c-be1d73aba173'
}

const Map = ({
  objects = [],
  mouseEvents = true,
  touchEvents = true,
  withCloseButton,
  onCloseClicked,
  onSearchHereClicked,
  withSearchHereButton,
  id,
  zoomClass,
  dpr,
  width,
  minZoom,
  defaultWidth,
  height,
  top,
  zoom,
  center,
  isGeolocating,
  scrollProtect,
  geolocate,
  onBoundsChanged,
  onAnimationStart,
  onAnimationStop,
  setZoom,
  setRef,
  onClick
}) => {
  const { i18nLocale } = useSelector(railsContext)

  const preloadImagesFunc = useCallback(() => {
    const finalDpr = typeof window !== 'undefined' ? window.devicePixelRatio || dpr : dpr
    const dprExtension = finalDpr >= 2 ? '@2x' : ''

    const images = [
      `/images/map/pin-green-large${dprExtension}.png`,
      `/images/map/pin-green-large-hover${dprExtension}.png`
    ]

    preloadImages(images)
  }, [dpr])

  useEffect(() => {
    preloadImagesFunc()
  }, [preloadImagesFunc])

  const mapProvider = (x, y, z, dpr) => {
    const retina = dpr && dpr >= 2 ? '@2x' : ''
    const mapId = mapTilerLocales[i18nLocale || I18n.locale] || mapTilerLocales.en
    return `https://api.maptiler.com/maps/${mapId}/256/${z}/${x}/${y}${retina}.png?key=${MAPTILER_ACCESS_TOKEN}`
  }

  const zoomIn = () => {
    zoom < 18 && setZoom && setZoom(zoom + 1)
  }

  const zoomOut = () => {
    zoom > 1 && setZoom && setZoom(zoom - 1)
  }

  return (
    <div
      id={id}
      className={`shared-map ${top ? 'with-top' : 'without-top'}`}
      style={{ width: '100%', height: height ? `${height}px` : '100%', position: 'relative' }}
    >
      <div style={{ width: '100%', height: height ? `${height}px` : '100%', position: 'absolute', zIndex: 1 }}>
        <PigeonMap
          mouseEvents={mouseEvents}
          touchEvents={touchEvents}
          minZoom={minZoom}
          ref={setRef}
          center={center}
          zoom={zoom}
          width={width}
          defaultWidth={defaultWidth}
          height={height}
          provider={mapProvider}
          dprs={[1, 2]}
          zoomSnap
          attribution={<Attribution />}
          attributionPrefix={false}
          onClick={onClick}
          onBoundsChanged={onBoundsChanged}
          onAnimationStart={onAnimationStart}
          onAnimationStop={onAnimationStop}
          metaWheelZoom={scrollProtect}
          twoFingerDrag={scrollProtect}
        >
          {objects}
        </PigeonMap>
      </div>
      {geolocate && <GeolocateButton withMap isGeolocating={isGeolocating} onClick={geolocate} />}
      {setZoom && (
        <span className={`map-button map-zoom-in-button${zoomClass ? ' ' + zoomClass : ''}`} onClick={zoomIn}>
          +
        </span>
      )}
      {setZoom && (
        <span className={`map-button map-zoom-out-button${zoomClass ? ' ' + zoomClass : ''}`} onClick={zoomOut}>
          –
        </span>
      )}
      {withSearchHereButton && (
        <button className='full-screen-map-button search-here-button' onClick={onSearchHereClicked}>
          {I18n.t('react.search.map.search_here')}
        </button>
      )}
      {withCloseButton && <Close className='full-screen-map-button' onClick={onCloseClicked} color='white' />}
    </div>
  )
}

Map.propTypes = {
  objects: PropTypes.array,
  center: PropTypes.array,
  zoomClass: PropTypes.string,
  id: PropTypes.string,
  dpr: PropTypes.number,
  width: PropTypes.number,
  minZoom: PropTypes.number,
  defaultWidth: PropTypes.number,
  height: PropTypes.number,
  zoom: PropTypes.number,
  onCloseClicked: PropTypes.func,
  onSearchHereClicked: PropTypes.func,
  geolocate: PropTypes.func,
  onBoundsChanged: PropTypes.func,
  onAnimationStart: PropTypes.func,
  onAnimationStop: PropTypes.func,
  setZoom: PropTypes.func,
  setRef: PropTypes.func,
  onClick: PropTypes.func,
  withSearchHereButton: PropTypes.bool,
  mouseEvents: PropTypes.bool,
  top: PropTypes.bool,
  isGeolocating: PropTypes.bool,
  scrollProtect: PropTypes.bool,
  touchEvents: PropTypes.bool,
  withCloseButton: PropTypes.bool
}

export default React.memo(Map)
